// Replace the entire file with this version
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Container,
  List,
  ListItemAvatar,
  Avatar,
  ListItemText,
  ListItemButton,
  TextField,
  Tooltip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useMediaQuery,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AddIcon from '@mui/icons-material/Add';
import FactCheckComponent from './FactCheckComponent';
import Header from './Header';
import { FetchUserData } from '../services/FetchUserData';
import { Link as RouterLink } from 'react-router-dom';
import { alpha } from '@mui/material/styles';


const appBarHeight = 64;
const MAX_HISTORY = 200;

const XFactualApp = ({ darkMode, toggleDarkMode, toggleDrawer }) => {
  const [claim, setClaim] = useState('');
  const [previousClaims, setPreviousClaims] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [user, setUser] = useState(null);
  const [filterVerdict, setFilterVerdict] = useState('all');
  const isMobile = useMediaQuery('(max-width: 900px)');

  const prevClaimsRef = useRef([]);
  useEffect(() => { prevClaimsRef.current = previousClaims; }, [previousClaims]);

  // Bootstrap user from localStorage (id, maybe username)
  useEffect(() => {
    let userObj = {};
    try {
      const storedUser = localStorage.getItem("xfactual_user");
      if (storedUser) userObj = JSON.parse(storedUser);
    } catch (error) {
      console.error("Error parsing xfactual_user from localStorage:", error);
    }
    try {
      const storedId = localStorage.getItem("xfactual_id");
      if (storedId) userObj = { ...userObj, id: isNaN(storedId) ? JSON.parse(storedId) : storedId };
    } catch (error) {
      console.error("Error parsing xfactual_id from localStorage:", error);
    }
    if (Object.keys(userObj).length > 0) setUser(userObj);
  }, []);

  const generateClaimSummary = useCallback((text = '') => {
    if (!text) return '';
    let s = String(text).replace(/```[\s\S]*?```/g, '').replace(/`([^`]+)`/g, '$1').trim();
    if (!s) return '';
    const words = s.split(/\s+/);
    const MAX_WORDS = 20;
    if (words.length <= MAX_WORDS) return s;
    return words.slice(0, MAX_WORDS).join(' ') + ' …';
  }, []);

  const normalizeClaimItem = useCallback((item) => {
    const claimObj = item.claim ?? item.claims ?? item.claim_data ?? (item.raw && (item.raw.claim || item.raw.claims)) ?? null;

    const id = (
      (claimObj && (claimObj.id || claimObj.claim_id || claimObj.claimId)) ||
      item.id ||
      item.claim_id ||
      (item.raw && item.raw.claim && (item.raw.claim.id || item.raw.claim.claim_id)) ||
      null
    );

    const fullText =
      (claimObj && (claimObj.claim_text || claimObj.claimText || claimObj.text || claimObj.title)) ||
      item.fullText ||
      item.claim_text ||
      item.text ||
      (item.raw && item.raw.claim && (item.raw.claim.claim_text || item.raw.claim.text)) ||
      '';

    const timestamp =
      (claimObj && (claimObj.created_at || claimObj.timestamp || claimObj.createdAt)) ||
      item.timestamp ||
      item.created_at ||
      (item.raw && item.raw.claim && item.raw.claim.created_at) ||
      '';

    const summary = item.text || item.claim_summary || item.summary || generateClaimSummary(fullText);

    const factCheck = item.fact_check || item.factCheck || item.fact_check_data || item.factCheckData || (item.raw && item.raw.fact_check) || null;
    const wikipedia = item.wikipedia || item.wiki || (item.raw && item.raw.wikipedia) || null;
    const newsArticles = item.news_articles || item.newsArticles || item.news || (item.raw && item.raw.news_articles) || [];

    const apiResp = item.apiResp || item.api_response_parsed || (item.user_ai_result && (item.user_ai_result.api_response_parsed || (function () {
      try { return item.user_ai_result.api_response ? JSON.parse(item.user_ai_result.api_response) : null; } catch { return null; }
    })())) || (item.raw && item.raw.user_ai_result && item.raw.user_ai_result.api_response_parsed) || null;

    const verdict =
      (factCheck && (factCheck.result || factCheck.verdict)) ||
      (apiResp && (apiResp.verdict || apiResp.truthRating)) ||
      item.verdict ||
      (item.raw && item.raw.fact_check && item.raw.fact_check.result) ||
      'inconclusive';

    return {
      id,
      text: summary || '(no summary)',
      fullText: fullText || '',
      timestamp: timestamp || '',
      factCheck,
      wikipedia,
      newsArticles,
      verdict,
      apiResp,
      raw: item,
      _optimistic: item._optimistic || false
    };
  }, [generateClaimSummary]);

  // Load history when user available; also hydrate username from the DB response if present
  useEffect(() => {
    let cancelled = false;
    async function loadHistory() {
      if (!user || !user.id) return;
      try {
        const raw = await FetchUserData(user.id);
        if (cancelled) return;

        // If API returns user info, pull username from it
        const dbUserName =
          raw?.user?.username ||
          raw?.user?.name ||
          raw?.username ||
          raw?.name ||
          raw?.user_name ||
          null;
        if (dbUserName) {
          setUser((prev) => ({ ...(prev || {}), username: dbUserName }));
        }

        const rawClaimsArray = Array.isArray(raw?.claims) ? raw.claims : (Array.isArray(raw) ? raw : (raw?.data || []));
        const normalized = rawClaimsArray.map(normalizeClaimItem);

        normalized.sort((a, b) => {
          const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
          const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
          return tb - ta;
        });

        const map = new Map();
        for (const it of normalized) {
          const key = it.id ?? `${it.fullText}:${it.timestamp}`;
          if (!map.has(key)) map.set(key, it);
        }

        const currentLocal = Array.isArray(prevClaimsRef.current) ? prevClaimsRef.current : [];
        for (const local of currentLocal) {
          if (local._optimistic) {
            const key = local.id ?? `${local.fullText}:${local.timestamp}`;
            if (!map.has(key)) {
              map.set(`optimistic_${key}_${Math.random().toString(36).slice(2, 8)}`, local);
            }
          }
        }

        const finalArray = Array.from(map.values()).slice(0, MAX_HISTORY);
        setPreviousClaims(finalArray);
      } catch (err) {
        console.error('FetchUserData failed:', err);
      }
    }
    loadHistory();
    return () => { cancelled = true; };
  }, [user, normalizeClaimItem]);

  // Load a previous claim’s result
  const handleLoadPreviousClaim = async (claimHistoryItem) => {
    try {
      setLoading(false); // ensure loading banner off for history loads
      const local = previousClaims.find(c => {
        if (!c) return false;
        if (c.id && claimHistoryItem.id) return String(c.id) === String(claimHistoryItem.id);
        return (c.text === claimHistoryItem.text && c.timestamp === claimHistoryItem.timestamp);
      });

      const buildApiOnlyResult = (base) => {
        const claimText = base?.fullText || base?.text || base?.claim_text || '';
        return {
          claim_id: base?.id ?? base?.claim_id ?? null,
          claim: { id: base?.id ?? null, claim_text: claimText },
          claim_text: claimText,
          api_response: base?.apiResp ?? null,
          raw: base?.raw ?? null
        };
      };

      if (local) {
        const hasFactCheck = Boolean(local.factCheck);
        if (!hasFactCheck) {
          setLoading(false);
          setResult(buildApiOnlyResult(local));
          return;
        }

        const localResult = {
          claim_id: local.id,
          claim: { id: local.id, claim_text: local.fullText || local.text },
          claim_text: local.fullText || local.text,
          verdict: (local.factCheck?.result ?? local.factCheck?.verdict) ?? (local.apiResp?.verdict ?? local.apiResp?.truthRating ?? local.verdict ?? null),
          confidence: local.factCheck?.confidence ?? local.apiResp?.confidence ?? null,
          justification: local.factCheck?.justification ?? local.apiResp?.justification ?? null,
          wikipedia: local.factCheck?.wikipedia ?? local.apiResp?.wikipedia ?? local.wikipedia ?? null,
          newsArticles: local.factCheck?.newsArticles ?? local.apiResp?.newsArticles ?? local.newsArticles ?? [],
          googleFactCheck: local.apiResp?.googleFactCheck ?? null,
          fact_check: local.factCheck ?? null,
          api_response: local.apiResp ?? null,
          raw: local.raw ?? null
        };
        setResult(localResult);
        setLoading(false);
        return;
      }

      const response = await fetch(`https://www.xfactual.org/api/get_claim_result.php?claim_id=${encodeURIComponent(claimHistoryItem.id)}`);
      if (!response.ok) throw new Error(`Fetch failed ${response.status}`);
      const data = await response.json();

      if (data.success) {
        const factCheck = data.fact_check ?? null;

        const parsedApiResp =
          safeParse(data?.user_ai_result?.api_response_parsed ?? data?.user_ai_result?.api_response) ??
          safeParse(data?.api_response) ??
          safeParse(data?.api_response_parsed) ??
          null;

        if (!factCheck) {
          setResult({
            claim_id: data.claim_id ?? data?.claim?.id ?? null,
            claim: data.claim ?? { id: data.claim_id ?? null, claim_text: data.claim_text ?? '' },
            claim_text: data.claim_text ?? data?.claim?.claim_text ?? '',
            api_response: parsedApiResp,
            raw: data
          });
          return;
        }

        const combinedResult = {
          claim_id: data.claim_id ?? data?.claim?.id ?? null,
          claim: data.claim ?? null,
          claim_text: data.claim_text ?? data?.claim?.claim_text ?? '',
          verdict: factCheck?.result ?? factCheck?.verdict ?? (parsedApiResp?.verdict ?? parsedApiResp?.truthRating ?? null),
          confidence: factCheck?.confidence ?? parsedApiResp?.confidence ?? null,
          justification: factCheck?.justification ?? parsedApiResp?.justification ?? null,
          wikipedia: factCheck?.wikipedia ?? data.wikipedia ?? parsedApiResp?.wikipedia ?? null,
          newsArticles: factCheck?.newsArticles ?? data.news_articles ?? parsedApiResp?.newsArticles ?? [],
          googleFactCheck: parsedApiResp?.googleFactCheck ?? null,
          fact_check: factCheck,
          api_response: parsedApiResp,
          raw: data
        };
        setResult(combinedResult);
      } else {
        console.error('Error fetching claim result:', data.error || data.message);
      }
    } catch (error) {
      console.error('Error loading previous claim:', error);
    }
  };

  // helper: local safeParse
  const safeParse = (maybe) => {
    if (maybe === null || maybe === undefined) return null;
    if (typeof maybe === 'object') return maybe;
    if (typeof maybe === 'string') {
      try { return JSON.parse(maybe); } catch { return null; }
    }
    return null;
  };

  // handleCheckClaimFunc - submission handler passed to FactCheckComponent
  // handleCheckClaimFunc - submission handler passed to FactCheckComponent
const handleCheckClaimFunc = async (claimOrForm) => {
  // If FactCheckComponent gave us a FormData (attachments), post it directly.
  const isFormData = typeof FormData !== 'undefined' && claimOrForm instanceof FormData;

  // Normalize the "text to send" for optimistic UI and summary
  const rawText = isFormData
    ? (claimOrForm.get('claim_text') || claim || '').toString()
    : (claimOrForm || claim || '').toString();

  const textToSend = rawText.trim();
  if (!textToSend) return null;

  const summary = generateClaimSummary(textToSend);
  const tempId = `temp_${Date.now()}`;

  // optimistic history + placeholder result (unchanged from your code)
  const newClaim = {
    id: tempId,
    text: summary || textToSend,
    fullText: textToSend,
    timestamp: new Date().toISOString(),
    factCheck: null,
    wikipedia: null,
    newsArticles: [],
    verdict: 'inconclusive',
    _optimistic: true
  };

  setPreviousClaims((prev) => {
    const dedup = (c) => (c.id ? String(c.id) : `${c.fullText}:${c.timestamp}`);
    const newList = [newClaim, ...prev.filter(c => dedup(c) !== dedup(newClaim))];
    const sliced = newList.slice(0, MAX_HISTORY);
    try { localStorage.setItem('xfactual_claims_cache_v1', JSON.stringify(sliced)); } catch (e) {}
    prevClaimsRef.current = sliced;
    return sliced;
  });

  setResult({
    claim_id: tempId,
    claim: { id: tempId, claim_text: textToSend },
    claim_text: textToSend,
    fact_check: null,
    apiResp: null,
    verdict: null,
    confidence: null,
    wikipedia: null,
    newsArticles: [],
    raw: { _optimistic: true }
  });
  setLoading(true);

  let data = null;
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://www.xfactual.org/api/fact_check.php';

    let bodyToSend;
    if (isFormData) {
      // Use the FormData from the child (already contains claim_text, user_id, and files)
      bodyToSend = claimOrForm;

      // Ensure we have a summary (helpful on server): add if absent
      if (!bodyToSend.get('claim_summary')) bodyToSend.append('claim_summary', summary);
      // Ensure user_id if available
      if (user && user.id && !bodyToSend.get('user_id')) bodyToSend.append('user_id', user.id);
    } else {
      // Original behavior: build FormData for plain text submit
      const formData = new FormData();
      formData.append('claim_text', textToSend);
      formData.append('claim_summary', summary);
      if (user && user.id) formData.append('user_id', user.id);
      bodyToSend = formData;
    }

    const response = await fetch(apiUrl, { method: 'POST', body: bodyToSend });
    if (!response.ok) throw new Error(`Network response was not ok: ${response.status}`);

    data = await response.json().catch(() => null);

    const parsedUserAi =
      safeParse(data?.user_ai_result?.api_response_parsed ?? data?.user_ai_result?.api_response) ??
      safeParse(data?.api_response) ??
      safeParse(data?.api_response_parsed) ??
      null;

    const normalizedResult = {
      claim_id: data?.claim_id ?? data?.claim?.id ?? data?.id ?? null,
      claim: data?.claim ?? { id: data?.claim_id ?? null, claim_text: data?.claim_text ?? textToSend },
      claim_text: data?.claim_text ?? data?.claim?.claim_text ?? textToSend,
      fact_check: data?.fact_check ?? null,
      apiResp: parsedUserAi,
      verdict:
        (data?.fact_check?.result) ??
        (data?.fact_check?.verdict) ??
        (parsedUserAi?.verdict) ??
        (parsedUserAi?.truthRating) ??
        null,
      confidence:
        (data?.fact_check?.confidence) ??
        (parsedUserAi?.confidence) ??
        null,
      justification:
        (data?.fact_check?.justification) ??
        (parsedUserAi?.justification) ??
        null,
      wikipedia:
        data?.fact_check?.wikipedia ??
        data?.wikipedia ??
        parsedUserAi?.wikipedia ??
        null,
      newsArticles:
        data?.fact_check?.newsArticles ??
        data?.news_articles ??
        parsedUserAi?.newsArticles ??
        parsedUserAi?.news ??
        [],
      raw: data
    };

    if (data && (data.success || data.claim_id)) {
      setPreviousClaims((prev) => {
        const mapped = prev.map((c) =>
          c.id === tempId ? { ...c, id: data.claim_id, factCheck: data.fact_check ?? c.factCheck, _optimistic: false } : c
        );
        try { localStorage.setItem('xfactual_claims_cache_v1', JSON.stringify(mapped.slice(0, MAX_HISTORY))); } catch (e) {}
        prevClaimsRef.current = mapped;
        return mapped;
      });
    } else if (data && (data.claim || data.user_ai_result || data.fact_check)) {
      setPreviousClaims((prev) => {
        const serverItem = {
          id: data.claim_id ?? data.claim?.id ?? null,
          text: generateClaimSummary((data.claim && (data.claim.claim_text || data.claim.text)) || data.claim_text || textToSend),
          fullText: (data.claim && (data.claim.claim_text || data.claim.text)) || data.claim_text || textToSend,
          timestamp: new Date().toISOString(),
          factCheck: data.fact_check ?? null,
          wikipedia: data.wikipedia ?? null,
          newsArticles: data.news_articles ?? [],
          verdict: (data.fact_check && (data.fact_check.result || data.fact_check.verdict)) || 'inconclusive',
          raw: data
        };
        const exists = prev.some((c) => (c.id && serverItem.id && String(c.id) === String(serverItem.id)) || (c.fullText === serverItem.fullText && c.timestamp === serverItem.timestamp));
        let merged = exists ? prev : [serverItem, ...prev];
        merged = merged.slice(0, MAX_HISTORY);
        prevClaimsRef.current = merged;
        try { localStorage.setItem('xfactual_claims_cache_v1', JSON.stringify(merged)); } catch (e) {}
        return merged;
      });
    }

    setResult(normalizedResult);

    if (user && user.id && (data?.claim_id || data?.claims)) {
      try {
        const fresh = await FetchUserData(user.id).catch(() => null);
        if (fresh && Array.isArray(fresh.claims) && fresh.claims.length > 0) {
          const normalizedFresh = fresh.claims.map(normalizeClaimItem);
          normalizedFresh.sort((a, b) => {
            const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
            const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
            return tb - ta;
          });
          const finalMap = new Map();
          for (const s of normalizedFresh) {
            const key = s.id ?? `${s.fullText}:${s.timestamp}`;
            finalMap.set(key, s);
          }
          const currentLocal = Array.isArray(prevClaimsRef.current) ? prevClaimsRef.current : [];
          for (const localItem of currentLocal) {
            if (localItem._optimistic) {
              const key = localItem.id ?? `${localItem.fullText}:${localItem.timestamp}`;
              if (!finalMap.has(key)) finalMap.set(key, localItem);
            }
          }
          const mergedArr = Array.from(finalMap.values()).slice(0, MAX_HISTORY);
          setPreviousClaims(mergedArr);
          try { localStorage.setItem('xfactual_claims_cache_v1', JSON.stringify(mergedArr.slice(0, 50))); } catch (e) {}
        }
      } catch (err) {
        console.warn('Error updating user history after submission:', err);
      }
    }
  } catch (error) {
    console.error('API request failed:', error);
    setResult({ error: error.message || 'Unknown network error', raw: data });
    setPreviousClaims((prev) =>
      prev.map((c) => (c.id === tempId ? { ...c, _optimistic: false, error: error.message || true } : c))
    );
  } finally {
    setLoading(false);
    return data;
  }
};


  const filteredClaims = previousClaims.filter(item => {
    const matchesSearch = (item.text || '').toLowerCase().includes(searchTerm.toLowerCase());
    if (filterVerdict === 'all') return matchesSearch;
    return matchesSearch && item.verdict && item.verdict.toLowerCase() === filterVerdict.toLowerCase();
  });

  const leftPanelWidth = isMobile ? 0 : 320;

  return (
    <>
      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} toggleDrawer={toggleDrawer} showSearchBar={true} />

      {!isMobile && (
        <Box
          sx={{
            overflowY: 'auto',
            '&::-webkit-scrollbar': { width: '8px' },
            '&::-webkit-scrollbar-track': { backgroundColor: darkMode ? '#222' : '#ddd' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: darkMode ? '#444' : '#888', borderRadius: '4px' },
            scrollbarColor: darkMode ? '#444 #222' : '#888 #ddd',
            scrollbarWidth: 'thin',
            position: 'fixed',
            top: appBarHeight,
            left: 0,
            bottom: 0,
            width: leftPanelWidth,
            bgcolor: darkMode ? '#000' : 'grey.100',
            borderRight: 1,
            borderColor: darkMode ? '#000' : 'divider',
            p: 2
          }}
          role="navigation"
          aria-label="Previous claims history"
        >
          {/* NEW: Welcome banner pulled from DB/local */}
          {/* Welcome chip + actions (before History) */}
<Box
  sx={{
    display: 'flex',
    alignItems: 'center',
    mb: 1.25,
    gap: 1,
  }}
>
  <Box
    sx={{
      px: 1.25,
      py: 0.9,
      borderRadius: 2,
      flex: 1,
      // glassy
      bgcolor: (t) => alpha(t.palette.background.paper, darkMode ? 0.18 : 0.55),
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      border: (t) =>
        `1px solid ${alpha(
          darkMode ? t.palette.common.white : t.palette.common.black,
          darkMode ? 0.12 : 0.10
        )}`,
      boxShadow: darkMode
        ? '0 6px 16px rgba(0,0,0,.28)'
        : '0 6px 16px rgba(0,0,0,.10)',
    }}
  >
    <Typography
      variant="subtitle2"
      sx={{
        fontWeight: 700,
        color: darkMode ? 'orange' : 'primary.main',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
      aria-label={user ? `Welcome, ${user.username}` : 'Welcome, Guest'}
    >
      Welcome, {user?.username ?? 'Guest'}!
    </Typography>
  </Box>

  <Tooltip title="Trending Claims (Coming Soon)" arrow>
    <IconButton
      size="small"
      aria-label="Trending Claims"
      sx={{ color: darkMode ? 'orange' : 'primary.main' }}
    >
      <TrendingUpIcon fontSize="small" />
    </IconButton>
  </Tooltip>

  <Tooltip title="Add New Claim (Coming Soon)" arrow>
    <IconButton
      size="small"
      aria-label="Add New Claim"
      sx={{ color: darkMode ? 'orange' : 'primary.main' }}
    >
      <AddIcon fontSize="small" />
    </IconButton>
  </Tooltip>
</Box>

{/* History header (no icons here now) */}
<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
  <Typography
    variant="h6"
    sx={{ color: darkMode ? 'orange' : 'primary.main', fontWeight: 'bold' }}
  >
    History
  </Typography>
</Box>

          


          <TextField
            placeholder="Search claims..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mt: 1, mb: 1, width: '100%', '& .MuiOutlinedInput-root': { backgroundColor: darkMode ? '#000' : 'white' } }}
            inputProps={{ 'aria-label': 'Search previous claims' }}
          />

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Filter by Verdict</InputLabel>
            <Select value={filterVerdict} label="Filter by Verdict" onChange={(e) => setFilterVerdict(e.target.value)}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="true">True</MenuItem>
              <MenuItem value="false">False</MenuItem>
              <MenuItem value="misleading">Misleading</MenuItem>
              <MenuItem value="inconclusive">Inconclusive</MenuItem>
            </Select>
          </FormControl>

          {filteredClaims.length === 0 ? (
            <Typography variant="body2" sx={{ color: darkMode ? '#000' : 'text.secondary' }}>No matching claims.</Typography>
          ) : (
            <List>
              {filteredClaims.map((item, index) => (
                <ListItemButton
                  key={item.id ?? index}
                  onClick={() => handleLoadPreviousClaim(item)}
                  aria-label={`Load previous claim: ${item.text}`}
                  sx={{ borderRadius: 1, mb: 1, backgroundColor: darkMode ? '#000' : 'grey.50', '&:hover': { backgroundColor: darkMode ? '#000' : 'grey.200' }, opacity: item._optimistic ? 0.95 : 1 }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: darkMode ? 'orange' : 'primary.main' }}>
                      <HistoryIcon fontSize="small" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<Typography variant="body2" sx={{ color: darkMode ? '#fff' : 'text.primary' }}>{item.text || '(no summary)'}</Typography>}
                    secondary={<Typography variant="caption" sx={{ color: darkMode ? '#ccc' : 'text.secondary' }}>{item.timestamp}</Typography>}
                  />
                </ListItemButton>
              ))}
            </List>
          )}

          <Typography variant="caption" sx={{ mt: 0.5 }}>
            <RouterLink to="/privacy-policy" style={{ textDecoration: 'none', color: 'whitesmoke' }}>Privacy Policy</RouterLink>
          </Typography>
        </Box>
      )}

      <Container
        sx={{
          ml: { xs: 0, md: `${leftPanelWidth + 20}px` },
          pr: { xs: 2, md: '10%' },
          mt: 2,
          pb: { xs: '120px', md: '160px' },
          height: `calc(100vh - ${appBarHeight + 32}px)`,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Removed the center-pane "Welcome" so it's only in the left rail */}

        <Box sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
          <FactCheckComponent
            claim={claim}
            setClaim={setClaim}
            result={result}
            loading={loading}
            handleCheckClaim={handleCheckClaimFunc}
            user={user}
            darkMode={darkMode}
            leftPanelWidth={leftPanelWidth}
            centerMaxWidth={1100}
            //userName={user?.username ?? 'Guest'}   // optional, if your FactCheckComponent uses it
          />
        </Box>
      </Container>
    </>
  );
};

export default XFactualApp;

